import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { newPasswordSchema, type NewPasswordFormValues } from '@/modules/auth/schemas/auth.schemas';
import { apiClient, isApiError } from '@/shared/lib/apiClient';
import { PasswordStrength } from '@/modules/auth/components/PasswordStrength';
import { Button } from '@/shared/components/ui/button';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { Label } from '@/shared/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

const searchSchema = z.object({
  token: z.string().default(''),
});

export const Route = createFileRoute('/auth/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordFormValues>({
    resolver: zodResolver(newPasswordSchema),
  });

  const password = watch('newPassword', '');

  const onSubmit = async (values: NewPasswordFormValues) => {
    setServerError(null);
    try {
      await apiClient.post('/api/v1/auth/reset-password', {
        token,
        newPassword: values.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      if (isApiError(err) && (err.status === 400 || err.status === 404)) {
        setServerError(t('auth.resetPassword.invalidToken'));
      } else if (isApiError(err) && err.status === 429) {
        setServerError(t('auth.errors.tooManyRequests', { seconds: err.retryAfterSeconds ?? 60 }));
      } else {
        setServerError(t('auth.errors.generic'));
      }
    }
  };

  if (!token) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          <Alert variant="destructive">
            <AlertDescription>{t('auth.resetPassword.invalidToken')}</AlertDescription>
          </Alert>
          <p className="text-center">
            <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
              {t('auth.forgotPassword.title')}
            </Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  if (success) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('auth.resetPassword.successTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="success">
            <AlertDescription>{t('auth.resetPassword.successMessage')}</AlertDescription>
          </Alert>
          <Link
            to="/auth/login"
            className="block text-center text-sm text-primary hover:underline font-medium"
          >
            {t('auth.resetPassword.signIn')}
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auth.resetPassword.title')}</CardTitle>
        <CardDescription>{t('auth.resetPassword.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {serverError && (
            <Alert variant="destructive">
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="newPassword">{t('auth.resetPassword.password')}</Label>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              placeholder={t('auth.resetPassword.passwordPlaceholder')}
              aria-invalid={!!errors.newPassword}
              {...register('newPassword')}
            />
            <PasswordStrength password={password} />
            {errors.newPassword && (
              <p className="text-xs text-destructive">{t(errors.newPassword.message ?? '')}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">{t('auth.resetPassword.confirmPassword')}</Label>
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
              aria-invalid={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">{t(errors.confirmPassword.message ?? '')}</p>
            )}
          </div>

          <Button type="submit" className="w-full" isLoading={isSubmitting}>
            {isSubmitting ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
